import { useState, useEffect, useCallback } from 'react';
import { Sensitive } from '../components/Sensitive';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Users, FileText, Activity, ArrowUpRight,
  Sparkles, Building2, Ticket, GraduationCap,
  MessageSquare, ExternalLink, ChevronRight, Zap, UserPlus, Plus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { CARRIER_EAPPS } from '../data/carrierEApps';
import { TasksWidget } from '../components/dashboard/TasksWidget';
import { RecentActivity } from '../components/dashboard/RecentActivity';
import { LeaderboardCard } from '../components/dashboard/LeaderboardCard';
import { PromotionWidget, BonusWidget, PromoBonusCarousel } from '../components/dashboard/PromoBonusWidgets';
import { SubmitSaleModal } from '../components/dashboard/SubmitSaleModal';
import { SubmitLeaderboardCard } from '../components/dashboard/SubmitLeaderboardCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPI_GRADIENTS = {
  blue: 'bg-gradient-to-br from-[#4318FF] to-[#868CFF]',
  green: 'bg-gradient-to-br from-[#01B574] to-[#43E97B]',
  purple: 'bg-gradient-to-br from-[#7000FF] to-[#B266FF]',
  orange: 'bg-gradient-to-br from-[#FF9000] to-[#FFB74D]',
};

const CARD = 'rounded-[20px] bg-white shadow-[0px_18px_40px_rgba(112,144,176,0.12)]';

export default function Dashboard() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);

  useEffect(() => {
    fetchStats();
    fetchLeaderboard();
  }, []);

  const fetchStats = async () => {
    try {
      const [productionRes, teamRes] = await Promise.all([
        axios.get(`${API}/production/stats`, getAuthHeader()),
        axios.get(`${API}/hierarchy/downline`, getAuthHeader())
      ]);
      setStats({
        production: productionRes.data,
        teamSize: teamRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard/top-producers?period=month&limit=10`, getAuthHeader());
      setLeaderboard(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setLeaderboard([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Issued Policies YTD',
      value: (stats?.production?.total_policies_ytd ?? stats?.production?.total_policies ?? 0).toLocaleString(),
      sub: 'Year to date',
      icon: FileText,
      gradient: KPI_GRADIENTS.green
    },
    {
      title: 'Writing Agents MTD',
      value: stats?.production?.writing_agents_mtd != null
        ? `${stats.production.writing_agents_mtd} / ${stats.production.total_active_agents ?? 0}`
        : '—',
      sub: 'This month',
      icon: Activity,
      gradient: KPI_GRADIENTS.purple
    },
    {
      title: 'Team Size',
      value: (stats?.teamSize || 0).toLocaleString(),
      sub: 'Active downline',
      icon: Users,
      gradient: KPI_GRADIENTS.orange
    }
  ];

  const quickActions = [
    {
      title: 'Breeze Leadz',
      sub: 'Purchase leads',
      icon: UserPlus,
      tile: 'bg-emerald-50 hover:bg-emerald-100/80',
      chip: 'bg-[#01B574]',
      onClick: () => navigate('/leads'),
      testId: 'quick-action-breeze-leadz'
    },
    {
      title: 'Training Hub',
      sub: 'Learn & grow',
      icon: GraduationCap,
      tile: 'bg-purple-50 hover:bg-purple-100/80',
      chip: 'bg-[#7000FF]',
      onClick: () => window.open('https://drive.google.com/drive/folders/1hQt8n-yIcpCSwpLzbRFzszNoX7e-EHuJ?usp=drive_link', '_blank', 'noopener,noreferrer'),
      external: true,
      testId: 'quick-action-training'
    },
    {
      title: 'IUL Script',
      sub: 'Talk-track guide',
      icon: MessageSquare,
      tile: 'bg-orange-50 hover:bg-orange-100/80',
      chip: 'bg-[#FF9000]',
      onClick: () => window.open('https://www.notion.so/Breeze-IUL-Script-247423bc43ce8013b4facd3fd4f57f52', '_blank', 'noopener,noreferrer'),
      external: true,
      testId: 'quick-action-iul-script'
    }
  ];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#2447D6] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-6" data-testid="dashboard">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-start justify-between gap-4"
      >
        <div>
          <h1 className="font-['Manrope'] text-2xl font-extrabold tracking-tight text-[#2B3674] sm:text-3xl">
            Welcome back, {user?.name} 👋
          </h1>
          <p className="mt-1 text-sm text-[#A3AED0] sm:text-base">
            Here's what's happening with your business today.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setSaleModalOpen(true)}
          data-testid="submit-sale-button"
          className="group relative flex items-center gap-2.5 overflow-hidden rounded-full bg-gradient-to-r from-[#23ACD5] via-[#00D0F5] to-[#00E5FF] px-6 py-4 font-['Manrope'] text-sm font-extrabold uppercase tracking-wide text-[#06263A] shadow-[0_0_0_1px_rgba(0,229,255,0.5),0_10px_34px_rgba(35,172,213,0.55)] transition-transform hover:scale-[1.03] active:scale-[0.99] sm:text-base"
        >
          <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/45 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          <Plus className="relative h-5 w-5" strokeWidth={3} />
          <span className="relative">Submit a Sale</span>
        </button>
      </motion.div>

      <SubmitSaleModal
        open={saleModalOpen}
        onOpenChange={setSaleModalOpen}
        onSubmitted={() => setBoardRefreshKey((k) => k + 1)}
      />

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
            >
              <div
                className={`relative overflow-hidden rounded-[20px] p-5 ${stat.gradient} shadow-[0px_18px_40px_rgba(112,144,176,0.18)]`}
                data-testid={`stat-card-${index}`}
              >
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
                <div className="relative flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
                    <Icon className="h-6 w-6 text-white" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-white/85">{stat.title}</p>
                    <p className="mt-0.5 truncate font-['Manrope'] text-[26px] font-extrabold leading-tight text-white">
                      <Sensitive>{stat.value}</Sensitive>
                    </p>
                  </div>
                </div>
                <p className="relative mt-3 text-[13px] font-medium text-white/75">{stat.sub}</p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Submit Leaderboard + Quick Actions */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Submit Leaderboard — replaces the Issued AP production chart */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="xl:col-span-2"
        >
          <SubmitLeaderboardCard refreshKey={boardRefreshKey} />
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24 }}
          className={`${CARD} p-6`}
          data-testid="quick-actions-card"
        >
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
              <Zap className="h-5 w-5 text-[#2447D6]" strokeWidth={2} />
            </div>
            <h3 className="font-['Manrope'] text-lg font-bold text-[#2B3674]">Quick Actions</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Carrier E-App dropdown tile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="quick-action-carrier-eapp"
                  className="group rounded-2xl bg-blue-50 p-4 text-left transition-colors hover:bg-blue-100/80"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#2447D6] shadow-sm">
                    <Building2 className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-sm font-bold text-[#2B3674]">Carrier E-App</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-[#A3AED0]">
                    Launch portal <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </p>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-96 w-72 overflow-y-auto" data-testid="quick-action-carrier-menu">
                <DropdownMenuLabel className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#2447D6]" />
                  Select Carrier
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {CARRIER_EAPPS.map((c) => (
                  <DropdownMenuItem
                    key={c.name}
                    onClick={() => window.open(c.url, '_blank', 'noopener,noreferrer')}
                    className="group flex cursor-pointer items-center justify-between gap-2"
                  >
                    <span className="text-sm">{c.name}</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-[#2447D6]" />
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.title}
                  type="button"
                  onClick={action.onClick}
                  data-testid={action.testId}
                  className={`group rounded-2xl p-4 text-left transition-colors ${action.tile}`}
                >
                  <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl shadow-sm ${action.chip}`}>
                    <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <p className="text-sm font-bold text-[#2B3674]">{action.title}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-[#A3AED0]">
                    {action.sub}
                    {action.external
                      ? <ExternalLink className="h-3 w-3" />
                      : <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />}
                  </p>
                </button>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Promotions & Bonuses — swipeable carousel on mobile, side-by-side grid on md+ */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        data-testid="dashboard-promo-bonus-row"
      >
        <PromoBonusCarousel />
      </motion.div>

      {/* Bottom widgets */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36 }}>
          <LeaderboardCard leaderboard={leaderboard} loading={leaderboardLoading} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42 }}>
          <TasksWidget />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.48 }} className="lg:col-span-2 xl:col-span-1">
          <RecentActivity />
        </motion.div>
      </div>
    </div>
  );
}
